import React from "react";
import { Dialog } from "@mui/material";
import CustomPaper from "../../common/CustomPaper";
import { NodeSelect, NodeTextField } from "../../NodeUI";
import {
  MISSION_AGENT_TEMPLATES,
  resolveMissionTemplatePolicy,
} from "../agentConfig/tabs/mission/missionTemplateDefaults";
import MissionPolicyFields from "./MissionPolicyFields";
import { policyDraftFromEffectivePolicy, policyDraftFromPolicy, policyFromDraft } from "./policy/missionPolicyDraft";
import { listMissionTemplates } from "./missionTemplatesApi";
import "./Missions.css";

const BUILT_IN_OPTIONS = MISSION_AGENT_TEMPLATES.map((t) => ({
  value: t.value,
  label: t.label,
}));

/**
 * Per-session mission configuration (harness plan Phase 8 + plan 2 Phase 5 tenant templates). Pick a
 * template (built-in or tenant-authored), tweak the editable policy, and start. Only the edited
 * policy is posted as an override; it is validated against platform safety caps and frozen for this
 * mission.
 */
export default function SessionMissionDialog({
  open,
  onClose,
  onStart,
  busy = false,
  error,
  templateId = "standard_agent",
}) {
  const [tenantTemplates, setTenantTemplates] = React.useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(templateId);
  const [draft, setDraft] = React.useState(null);

  // Resolves a template id to its policy: built-ins from the local catalog, tenant templates from
  // the fetched list.
  const policyForTemplate = React.useCallback(
    (id) => {
      const tenant = tenantTemplates.find((t) => t.id === id);
      if (tenant) return tenant.policy || {};
      return resolveMissionTemplatePolicy(id);
    },
    [tenantTemplates]
  );

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listMissionTemplates()
      .then((items) => {
        if (!cancelled) setTenantTemplates(items);
      })
      .catch(() => {
        if (!cancelled) setTenantTemplates([]);
      });
    setSelectedTemplateId(templateId);
    setDraft({
      name: "",
      objective: "",
      ...policyDraftFromEffectivePolicy({}, templateId),
    });
    return () => {
      cancelled = true;
    };
  }, [open, templateId]);

  if (!draft) {
    return (
      <Dialog open={open} onClose={onClose} PaperComponent={CustomPaper} maxWidth="sm" fullWidth />
    );
  }

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    setDraft((d) => ({
      ...d,
      ...policyDraftFromPolicy(policyForTemplate(id)),
    }));
  };

  const templateOptions = [
    ...BUILT_IN_OPTIONS,
    ...tenantTemplates.map((t) => ({ value: t.id, label: t.name })),
  ];

  const handleStart = () => {
    onStart({
      objective: draft.objective.trim() || undefined,
      name: draft.name.trim() || undefined,
      templateId: selectedTemplateId,
      policy: policyFromDraft(draft),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      PaperComponent={CustomPaper}
      maxWidth="sm"
      fullWidth
    >
      <div className="mission-dialog">
        <div className="mission-dialog__header">
          <h2 className="mission-dialog__title">Run this session as a mission</h2>
          <p className="mission-dialog__subtitle">
            Configure this session's mission. Values are validated against platform
            safety caps and frozen for this mission only.
          </p>
        </div>

        <div className="mission-dialog__body">
          <NodeTextField
            label="Name (optional)"
            value={draft.name}
            onChange={(v) => set("name", v)}
            placeholder="e.g. Weekly cleanup"
          />
          <NodeTextField
            label="Objective"
            value={draft.objective}
            onChange={(v) => set("objective", v)}
            placeholder="What should this mission accomplish?"
            multiline
            minRows={2}
            maxRows={4}
          />

          {templateOptions.length > 1 && (
            <NodeSelect
              label="Template"
              value={selectedTemplateId}
              options={templateOptions}
              inDialog
              onChange={handleTemplateChange}
            />
          )}

          <MissionPolicyFields draft={draft} onChange={set} />
        </div>

        <div className="mission-dialog__footer">
          {error ? <p className="mission-dialog__error">{error}</p> : null}

          <div className="mission-form-actions">
            <button
              type="button"
              className="mission-btn mission-btn--ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="mission-btn mission-btn--primary"
              onClick={handleStart}
              disabled={busy}
            >
              {busy ? "Starting…" : "Start mission"}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
